import { faCalendarAlt, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { scrollDate } from 'helpers/scrollDate';
import { formatDate } from 'helpers/formatDate';
import { DatePicker } from 'components/DatePicker';
import { Button } from 'components/Button';
import styles from './DateRangePicker.scss';

export const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [presetRange, setPresetRange] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const presets = [
    { label: 'Сьогодні', days: 0 },
    { label: 'Останні 7 днів', days: 6 },
    { label: 'Останні 14 днів', days: 13 },
    { label: 'Останній місяць', days: 30 },
    { label: 'Останні 3 місяці', days: 90 },
  ];

  useEffect(() => {
    if (!startDate || !endDate) {
      setPresetRange(null);
      return;
    }

    const normalize = (d, endOfDay = false) => {
      const date = new Date(d);
      if (endOfDay) {
        date.setHours(23, 59, 59, 999);
      } else {
        date.setHours(0, 0, 0, 0);
      }
      return date;
    };

    const normalizedStart = normalize(startDate);
    const normalizedEnd = normalize(endDate, true);
    const today = normalize(new Date());
    const diffDays = Math.round((today - normalizedStart) / (1000 * 60 * 60 * 24));

    const found = presets.find(
      (p) => normalizedEnd.getTime() === today.setHours(23, 59, 59, 999) && diffDays === p.days
    );

    setPresetRange(found ? found.days : null);
  }, [startDate, endDate]);

  const handleApply = () => {
    onChange({
      startDate: tempStartDate,
      endDate: tempEndDate,
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  const changePresetRange = (days) => {
    setPresetRange(days);
    const end = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    start.setDate(start.getDate() - days);

    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleStartDateChange = useCallback((direction) => {
    setTempStartDate((prev) => {
      const newDate = new Date(scrollDate(prev, direction));
      setPresetRange(null);
      return newDate;
    });
  }, []);

  const handleEndDateChange = useCallback((direction) => {
    setTempEndDate((prev) => {
      const newDate = new Date(scrollDate(prev, direction));
      setPresetRange(null);
      return newDate;
    });
  }, []);

  return (
    <div className={styles.dateRangePicker}>
      <button className={styles.trigger} onClick={() => setIsOpen(!isOpen)}>
        <FontAwesomeIcon icon={faCalendarAlt} />
        <span>
          {formatDate(startDate, 'UA')} — {formatDate(endDate, 'UA')}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`${styles.chevron} ${isOpen ? styles.open : ''}`} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <h4 className={styles.title}>Швидкий вибір:</h4>
          {presets.map((preset) => (
            <button
              key={preset.label}
              className={cn(styles.presetButton, { [styles.active]: preset.days === presetRange })}
              onClick={() => changePresetRange(preset.days)}
            >
              {preset.label}
              <FontAwesomeIcon className={styles.icon} icon={faCheck} />
            </button>
          ))}

          <h4 className={styles.title}>Користувацький діапазон:</h4>
          <div className={styles.dateInputs}>
            <div>
              <label className={styles.dateLabel}>Початкова дата:</label>
              <DatePicker
                className={styles.datePicker}
                date={tempStartDate}
                handleScrollDate={handleStartDateChange}
                enableCalendar
              />
            </div>
            <div>
              <label className={styles.dateLabel}>Кінцева дата:</label>
              <DatePicker
                className={styles.datePicker}
                date={tempEndDate}
                handleScrollDate={handleEndDateChange}
                enableCalendar
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button text='Скасувати' onClick={handleCancel} ghostStyle smallStyle />
            <Button text='Застосувати' onClick={handleApply} smallStyle />
          </div>
        </div>
      )}
    </div>
  );
};
